import { useState, useEffect, useRef } from "react";

/**
 * Find the first *visible* element matching a selector.
 * Elements with display:none or zero dimensions (hidden mobile sidebar etc.) are skipped.
 */
function findVisibleElement(selector: string): Element | null {
  const all = Array.from(document.querySelectorAll(selector));
  for (const el of all) {
    const r = el.getBoundingClientRect();
    // Skip elements that are hidden (display:none gives 0×0 rect)
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

/**
 * Polls for a DOM element by selector and returns its bounding rect.
 * Uses requestAnimationFrame for smooth tracking of position changes.
 * Skips hidden/zero-size elements (e.g. mobile sidebar duplicates).
 */
export function useTargetRect(selector: string | null): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const prevRef = useRef<string>("");

  useEffect(() => {
    if (!selector) {
      setRect(null);
      prevRef.current = "";
      return;
    }

    // Reset immediately when selector changes
    if (selector !== prevRef.current) {
      setRect(null);
      prevRef.current = selector;
    }

    let rafId: number;

    const update = () => {
      const el = findVisibleElement(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect((prev) => {
          if (
            !prev ||
            Math.abs(prev.top - r.top) > 0.5 ||
            Math.abs(prev.left - r.left) > 0.5 ||
            Math.abs(prev.width - r.width) > 0.5 ||
            Math.abs(prev.height - r.height) > 0.5
          ) {
            return DOMRect.fromRect({ x: r.x, y: r.y, width: r.width, height: r.height });
          }
          return prev;
        });
      } else {
        setRect(null);
      }
      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [selector]);

  return rect;
}

/** Export for use in SpotlightOverlay click handler */
export { findVisibleElement };
